import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NotificationPreferencesTable } from '../../../components/notifications/NotificationPreferencesTable';
import { useMyNotificationPreferences, useUpdateMyNotificationPreferences } from '../../../components/notifications/preferencesHooks';
import { useAuth } from '../../../contexts/authContext';
import { morgansClient } from '../../../lib/connect/clients';
import { useCanonicalOpportunities } from '../hooks/useCanonicalOpportunities';
import { watchClient } from './client';
/** The surrounding drawer is a Notice. Only SAD-469 canonical IDs reach Watch. */ export function OpportunityWatch({ noticeId, onNavigate }) {
    const list = useCanonicalOpportunities();
    const linkQuery = useQuery({
        queryKey: [
            'canonicalOpportunities',
            'links',
            noticeId
        ],
        queryFn: ()=>morgansClient.listCanonicalOpportunityNoticeLinks({
                noticeId
            }),
        enabled: !!noticeId
    });
    const links = {
        linked: linkQuery.data?.links ?? [],
        isLoading: !!noticeId && linkQuery.isLoading,
        error: linkQuery.error
    };
    const [params, setParams] = useSearchParams();
    const selected = params.get('watchOpportunity') ?? links.linked[0]?.opportunityId ?? '';
    const opportunity = list.data?.find((o)=>o.id === selected);
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Opportunity Watch",
        className: "space-y-3",
        "data-testid": Testids.opportunityWatch.section,
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-medium",
                children: "Opportunity Watch"
            }),
            list.isLoading || links.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading Opportunities…"
            }) : null,
            list.error || links.error ? /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Could not load Opportunities."
            }) : null,
            !list.error && !links.error && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Watch canonical Opportunity",
                        value: opportunity?.id ?? '',
                        "data-testid": Testids.opportunityWatch.picker,
                        onChange: (e)=>setParams((previous)=>{
                                const next = new URLSearchParams(previous);
                                next.set('watchOpportunity', e.target.value);
                                next.delete('watchEvent');
                                return next;
                            }),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Select an Opportunity…"
                            }),
                            (list.data ?? []).map((o)=>/*#__PURE__*/ _jsxs("option", {
                                    value: o.id,
                                    children: [
                                        o.title,
                                        links.linked.some((l)=>l.opportunityId === o.id) ? ' (linked to this Notice)' : ''
                                    ]
                                }, o.id))
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Choose a business thread, including manual Opportunities without a Notice. Selecting does not associate evidence."
                    }),
                    opportunity && /*#__PURE__*/ _jsx(WatchDetail, {
                        id: opportunity.id,
                        title: opportunity.title,
                        onNavigate: onNavigate
                    }, opportunity.id)
                ]
            })
        ]
    });
}
function WatchDetail({ id, title, onNavigate }) {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [params, setParams] = useSearchParams();
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const eventId = params.get('watchEvent') ?? '';
    const key = [
        'opportunityWatch',
        user?.tenant_id,
        user?.id,
        id
    ];
    const watch = useQuery({
        queryKey: key,
        queryFn: ()=>watchClient.getOpportunityWatch({
                opportunityId: id
            }),
        retry: false
    });
    const activity = useQuery({
        queryKey: [
            ...key,
            'activity'
        ],
        queryFn: ()=>watchClient.listOpportunityActivity({
                opportunityId: id
            }),
        retry: false
    });
    const event = useQuery({
        queryKey: [
            ...key,
            'event',
            eventId
        ],
        queryFn: ()=>watchClient.getOpportunityActivity({
                opportunityId: id,
                id: eventId
            }),
        enabled: !!eventId,
        retry: false
    });
    const update = useMutation({
        mutationFn: (watching)=>watchClient.setOpportunityWatch({
                opportunityId: id,
                watching
            }),
        onSuccess: (result)=>qc.setQueryData(key, result)
    });
    // Fail closed: no cached event or Watch state survives a current denial/error.
    if (watch.error || activity.error) return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: "Watch and activity unavailable. Access may be restricted."
    });
    if (watch.isLoading || activity.isLoading) return /*#__PURE__*/ _jsx("output", {
        children: "Loading Watch and activity…"
    });
    const watching = watch.data?.watch?.watching ?? false;
    const events = [
        ...activity.data?.activities ?? []
    ].sort((a, b)=>(a.recordedAt?.toDate().getTime() ?? 0) - (b.recordedAt?.toDate().getTime() ?? 0) || a.id.localeCompare(b.id));
    const selected = event.data?.activity;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-opportunity-id": id,
        children: [
            /*#__PURE__*/ _jsx("h4", {
                children: title
            }),
            /*#__PURE__*/ _jsx(Button, {
                disabled: update.isPending,
                "aria-pressed": watching,
                "data-testid": Testids.opportunityWatch.toggle,
                onClick: ()=>update.mutate(!watching),
                children: update.isPending ? 'Saving…' : watching ? 'Watching for me' : 'Watch Opportunity'
            }),
            update.error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Could not change your Watch. Try again."
            }),
            /*#__PURE__*/ _jsx(Button, {
                variant: "ghost",
                onClick: ()=>setPreferencesOpen((open)=>!open),
                children: "Notification preferences"
            }),
            preferencesOpen && /*#__PURE__*/ _jsx(WatchPreferences, {
                companyId: user?.tenant_id ?? ''
            }),
            /*#__PURE__*/ _jsx("h4", {
                children: "Market Activity"
            }),
            !events.length && /*#__PURE__*/ _jsx("p", {
                children: "No recorded activity."
            }),
            /*#__PURE__*/ _jsx("ol", {
                className: "space-y-2",
                "data-testid": Testids.opportunityWatch.timeline,
                children: events.map((item)=>/*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs(Button, {
                            type: "button",
                            variant: "ghost",
                            className: "h-auto whitespace-normal text-left underline",
                            "data-testid": Testids.opportunityWatch.event,
                            "data-event-id": item.id,
                            "aria-current": eventId === item.id ? 'true' : undefined,
                            onClick: ()=>setParams((previous)=>{
                                    const next = new URLSearchParams(previous);
                                    next.set('watchOpportunity', id);
                                    next.set('watchEvent', item.id);
                                    return next;
                                }),
                            children: [
                                item.summary,
                                " — ",
                                item.recordedAt?.toDate().toISOString() ?? 'Unknown record time'
                            ]
                        })
                    }, item.id))
            }),
            eventId && (event.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading event…"
            }) : event.error || !selected ? /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Event unavailable. Access may be restricted."
            }) : /*#__PURE__*/ _jsxs("aside", {
                "aria-label": "Source evidence",
                "data-testid": Testids.opportunityWatch.inspector,
                "data-event-id": selected.id,
                children: [
                    /*#__PURE__*/ _jsx("h5", {
                        children: selected.summary
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: selected.kind.replace(/_/g, ' ')
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Publication: ",
                            selected.publishedAt?.toDate().toISOString() ?? 'Unknown'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Retrieved: ",
                            selected.retrievedAt?.toDate().toISOString() ?? 'Unknown'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Associated: ",
                            selected.associatedAt?.toDate().toISOString() ?? 'Unknown'
                        ]
                    }),
                    selected.publicationId && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Source version: ",
                            selected.publicationId
                        ]
                    }),
                    selected.sourceVersion && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Source snapshot: ",
                            selected.sourceVersion
                        ]
                    }),
                    selected.sourceMaterialJson && /*#__PURE__*/ _jsx("pre", {
                        className: "whitespace-pre-wrap break-words",
                        children: selected.sourceMaterialJson
                    }),
                    selected.noticeId && /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        onClick: ()=>onNavigate(selected.noticeId),
                        children: "Open source Notice"
                    })
                ]
            }))
        ]
    });
}
function WatchPreferences({ companyId }) {
    const prefs = useMyNotificationPreferences(companyId);
    const update = useUpdateMyNotificationPreferences(companyId);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Personal notification preferences"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Watch activity uses Shared information. These controls edit your existing notification preferences."
            }),
            !companyId || prefs.error ? /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Notification preferences unavailable."
            }) : prefs.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading preferences…"
            }) : /*#__PURE__*/ _jsx(NotificationPreferencesTable, {
                kinds: prefs.data ?? [],
                ownSource: "PERSONAL",
                onUpdate: (value)=>update.mutate(value),
                pending: update.isPending
            }),
            update.error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Could not save preferences."
            })
        ]
    });
}
